import { CoreApiClient } from "./coreApiClient";
import type { QuotationTemplateDefinition } from "@/constants/quotationOptions";

const API_BASE_URL = (() => {
  const envValue = import.meta.env.VITE_API_BASE_URL;
  if (envValue && envValue.trim() !== "") {
    return envValue.trim();
  }
  return "/api";
})();

type ApiEnvelope<T> = {
  success: true;
  data: T;
  meta?: {
    warnings?: string[];
    [key: string]: unknown;
  };
};

export type QuotationSigningStatus = "active" | "processing" | "completed" | "expired" | "revoked" | "invalid";

export type QuotationSigningCheckboxes = {
  soilReport: boolean;
  legalPointOfDischarge: boolean;
  wsudReport: boolean;
  sewerInformation: boolean;
};

export type QuotationSigningFormData = {
  signedByName: string;
  clientName: string;
  clientCompany: string;
  clientAbn: string;
  clientAddress: string;
  clientPhone: string;
  clientEmail: string;
  paymentScheduleAcknowledged: boolean;
  checkboxes: QuotationSigningCheckboxes;
};

export type PublicQuotationSigningQuotation = {
  QuotationId: number | null;
  QuotationNumber: string | null;
  Date?: string | null;
  IssueDate?: string | null;
  Address?: string | null;
  Suburb?: string | null;
  PostCode?: string | number | null;
  Council?: string | null;
  Entity?: string | null;
  StructuralFee?: number | string | null;
  Structural?: number | string | null;
  CivilFee?: number | string | null;
  SoilReportFee?: number | string | null;
  HourlyRate?: number | string | null;
  InspectionFee?: number | string | null;
  QuoteValidityDays?: number | string | null;
  QuotationAmount?: number | string | null;
  SubTotal?: number | string | null;
  ProjectType?: string | null;
  ProjectDescription?: string | null;
  ProjectScope?: string | null;
  Client?: string | null;
  ClientName?: string | null;
  ClientCompany?: string | null;
  CompanyName?: string | null;
  ABN?: string | null;
  ClientAddress?: string | null;
  ClientMobile?: string | null;
  ClientEmail?: string | null;
  ScopeFootnoteS?: string | null;
  ScopeFootnoteC?: string | null;
  ExclusionsJson?: unknown;
  SenderDisplayName?: string | null;
  SenderAddress?: string | null;
  SenderMobile?: string | null;
  SenderEmail?: string | null;
  SenderPayId?: string | null;
  SenderBsb?: string | null;
  SenderAccountNumber?: string | null;
  SenderAccountName?: string | null;
  BroughtBy?: string | null;
  CertifiedBy?: string | null;
  StructuralPaymentScheduleJson?: unknown;
  CivilPaymentScheduleJson?: unknown;
  StructuralScopeGroupKey?: string | null;
  StructuralScopeGroupLabel?: string | null;
  StructuralScopeItemsJson?: unknown;
  CivilScopeGroupKey?: string | null;
  CivilScopeGroupLabel?: string | null;
  CivilScopeItemsJson?: unknown;
  GeneralScopeItemsJson?: unknown;
  IncludeGeneralScope?: boolean | number | string | null;
  TemplateSnapshotJson?: unknown;
};

export type PublicQuotationSigningSession = {
  sessionId: number | null;
  quotationId: number | null;
  quotationNumber: string | null;
  status: QuotationSigningStatus;
  issuedAt: string | null;
  expiresAt: string | null;
  completedAt: string | null;
  projectId: number | null;
  projectNumber: string | null;
  emailRecipients: string[];
  formDefaults: QuotationSigningFormData;
  quotation: PublicQuotationSigningQuotation | null;
  templateDefinition: Partial<QuotationTemplateDefinition> | null;
  completedSummary: {
    signedByName?: string | null;
    clientName?: string | null;
    clientCompany?: string | null;
    clientEmail?: string | null;
    clientPhone?: string | null;
  } | null;
};

export type SubmitQuotationSigningPayload = QuotationSigningFormData & {
  token: string;
  signatureDataUrl: string;
  pdfBase64?: string;
  pdfContentType?: string;
  pdfFileName?: string;
};

const coreClient = new CoreApiClient(API_BASE_URL, (input, init) => fetch(input, init));

export const quotationSigningClient = {
  buildReviewQuotationUrl(token: string) {
    const query = new URLSearchParams({ token });
    return `${API_BASE_URL}/v1/public/quotation-signing/review-file?${query.toString()}`;
  },

  buildReviewQuotationPdfUrl(token: string) {
    const query = new URLSearchParams({ token });
    return `${API_BASE_URL}/v1/public/quotation-signing/review-pdf?${query.toString()}`;
  },

  async getSession(token: string) {
    const response = await coreClient.request<ApiEnvelope<PublicQuotationSigningSession>>(
      `${API_BASE_URL}/v1/public/quotation-signing/session`,
      {
        method: "GET",
        query: { token },
      }
    );
    return response.data;
  },

  async submit(payload: SubmitQuotationSigningPayload) {
    const response = await coreClient.request<ApiEnvelope<PublicQuotationSigningSession>>(
      `${API_BASE_URL}/v1/public/quotation-signing/submit`,
      {
        method: "POST",
        body: payload,
      }
    );
    return {
      data: response.data,
      warnings: Array.isArray(response.meta?.warnings) ? response.meta?.warnings : [],
    };
  },
};
